import BirthdayTimeSlotJson, { validateBirthdayTimeSlotJson } from './BirthdayTimeSlotJson';
import { Month } from './Month';
import { Validation } from './Validation';
import { WeekDay } from './WeekDay';

export const BirthdayProgramDescriptionAllowedTags: string[] = ["p", "br", "b", "ul", "li", "u"];

export default interface BirthdayProgramJson {
   ID: number | null;

   /**
    * Kategorie des Programms.
    */
   category: string;

   /**
    * Kurzbeschreibung des Programms.
    */
   title: string;

   /**
    * Beschreibung des Programms.
    */
   description: string;

   /**
    * Besondere Hinweise die für dieses Programm gelten. Kann ein leerer String sein wenn nichts besonderes gilt.
    */
   remarks: string;

   /**
    * Gibt an ob ein Mitarbeiter benötigt wird.
    */
   isStaffRequired: boolean;

   /**
    * Basispreis zu dem dieses Programm angeboten wird in Eurocent. Gilt maximal für eine bestimme Teilnehmerzahl.
    * Der Preis steht ohnehin immer in den remarks beschrieben.
    */
   basePrice: number;

   /**
    * Teilnehmerzahl bis zu der der Basispreis gilt.
    */
   basePriceParticipantCount: number;

   /**
    * Kosten pro Teilnehmer für jeden Teilnehmer über der Teilnehmerzahl für den Basispreis.
    */
   extraParticipantPrice: number;

   /**
    * Kosten die bei einer Stornierung anfallen in Eurocent.
    */
   cancellationFee: number;

   /**
    * Stornierungsbedingungen.
    */
   cancellationRemarks: string;

   /**
    * Abweichende Uhrzeiten. Null wenn Standardzeiten verwendet werden sollen.
    */
   alternateTimeSlots: BirthdayTimeSlotJson[] | null;

   /**
    * Gibt an, welches Personal automatisch zugewiesen werden kann. Wenn der Wert null ist, kann jeder Mitarbeiter automatisch zugewiesen werden.
    */
   restrictStaffAssignmentIds: number[] | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, welche maximale Teilnehmerzahl für das Programm zugelassen sind. Kann null sein, falls keine Einschränkung besteht.
    */
   restrictMaximumParticipants: number | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, für welche Wochentage das Programm gebucht werden kann. Kann null sein, falls keine Einschränkungen bestehen.
    * Die Zahlen in dem enum WeekDay sind genau wie in new Date().getDay() definiert: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getDay
    */
   restrictWeekDays: WeekDay[] | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, für welche Monate das Programm gebucht werden kann. Kann null sein, falls keine Einschränkungen bestehen.
    * Die Zahlenwerte in dem enum Month sind genau wie in new Date().getMonth() definiert: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getMonth
    */
   restrictMonths: Month[] | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, welches Mindestalter die Kinder für das Programm haben sollten. Kann null sein, falls keine Einschränkung besteht.
    */
   restrictMinimumAge: number | null;

   /**
    * Gibt für das Kunden-Buchungsformular an, welches Maximalalter die Kinder für das Programm haben sollten. Kann null sein, falls keine Einschränkung besteht.
    */
   restrictMaximumAge: number | null;


   /**
    * Maximale Anzahl an der das Programm gleichzeitig gebucht werden kann.
    */
   restrictMaximumSimultaneous: number | null;
}

export function getDefaultBirthdayProgramData(): BirthdayProgramJson {
   return {
      ID: null,
      category: "",
      title: "",
      description: "",
      remarks: "",
      isStaffRequired: true,
      basePrice: 0,
      basePriceParticipantCount: 10,
      extraParticipantPrice: 0,
      cancellationFee: 0,
      cancellationRemarks: "",
      alternateTimeSlots: null,
      restrictStaffAssignmentIds: null,
      restrictMaximumParticipants: null,
      restrictWeekDays: null,
      restrictMonths: null,
      restrictMinimumAge: null,
      restrictMaximumAge: null,
      restrictMaximumSimultaneous: null
   }
}

export function getBirthdayProgramsGrouped(birthdayPrograms: BirthdayProgramJson[]): { [index: string]: BirthdayProgramJson[]; } {
   var groupedPrograms: { [index: string]: BirthdayProgramJson[]; } = {};
   
   for (let program of birthdayPrograms) {
      if (groupedPrograms.hasOwnProperty(program.category)) {
         groupedPrograms[program.category].push(program);
      } else {
         groupedPrograms[program.category] = [program];
      }
   }

   return groupedPrograms;
}

export function validateBirthdayProgramJson(item: BirthdayProgramJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateNonEmptyString("category", 100)
      .validateNonEmptyString("title", 100)
      .validateString("description", 1500)
      .validateString("remarks", 500)
      .validateInteger("basePrice", 0, null)
      .validateInteger("basePriceParticipantCount", 0, null)
      .validateAllUsingFunction("alternateTimeSlots", validateBirthdayTimeSlotJson, 1, true)
      .validateInteger("extraParticipantPrice", 0, null)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .validateInteger("restrictMaximumParticipants", 1, 100, true)
      .validateInteger("restrictMinimumAge", 0, 120, true)
      .validateInteger("restrictMaximumAge", 0, 120, true)
      .validateInteger("restrictMaximumSimultaneous", 0, 100, true)
      .finish();
}

export function validateBirthdayProgramJsonAddNew(item: BirthdayProgramJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateNonEmptyString("category", 100)
      .validateNonEmptyString("title", 100)
      .validateString("description", 1500)
      .validateString("remarks", 500)
      .validateInteger("basePrice", 0, null)
      .validateInteger("basePriceParticipantCount", 0, null)
      .validateAllUsingFunction("alternateTimeSlots", validateBirthdayTimeSlotJson, 1, true)
      .validateInteger("extraParticipantPrice", 0, null)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .validateInteger("restrictMaximumParticipants", 1, 100, true)
      .validateInteger("restrictMinimumAge", 0, 120, true)
      .validateInteger("restrictMaximumAge", 0, 120, true)
      .validateInteger("restrictMaximumSimultaneous", 0, 100, true)
      .finish();
}